

// Função utilitária para limpar campos de auditoria
function limparCamposAuditoria(obj) {
    const {
        createAt,
        createBy,
        updateAt,
        updateBy,
        deleteAt,
        deleteBy,
        isDeleted,
        ...rest
    } = obj;
    return rest;
}
import ERP from "../ERP.mjs";
import View from "../View.mjs";
import Schema from "../Schema.mjs";
import useUsuarioStore from "../stores/UsuarioStore";
import UtilArray from "@/utils/UtilArray.mjs";
import EntityService from "@/services/entityService";
import { buildOwnersWithAdmin } from "@/utils/ownerUtils.mjs";

const pegaId = (value = null) => {
    if (value && typeof value === 'object') return value.id || value._id || value.userId || value.valueId || '';
    return value || '';
};

const ehObjeto = (value = null) => value != null && typeof value === 'object' && !Array.isArray(value);

const temDadosDeItemDaOS = (item = {}) => {
    if (!ehObjeto(item)) return false;
    return !!(item?.sequencia || item?.descricao || item?.itemCodigoExt || item?.itemCodigo || item?.status);
};

const normalizaIdList = (ids = []) => {
    return Array.isArray(ids)
        ? ids.map((id) => pegaId(id)).filter((id) => !!id)
        : [];
};

const ehIdPersistido = (id = '') => {
    const texto = String(id || '').trim();
    if (!texto) return false;

    if (texto.startsWith('tmp-') || texto.startsWith('os-item-')) return false;

    const objectIdRegex = /^[a-f0-9]{24}$/i;
    if (objectIdRegex.test(texto)) return true;

    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (uuidRegex.test(texto)) return true;

    return texto.length >= 8;
};

const normalizaRegistroEntity = (registro = {}) => {

    if (!ehObjeto(registro)) {
        const id = pegaId(registro);
        return {
            id,
            _id: id,
            _childrens: {},
        };
    }

    const currentData = registro?._currentData || registro?.currentData || {};
    const childrens = registro?.childrens || registro?._childrens || {};
    const temDados = ehObjeto(currentData) && Object.keys(currentData).length > 0;
    const dados = temDados ? currentData : registro;

    return {
        ...dados,
        id: dados?.id || dados?._id || registro?.id || registro?._id || '',
        _id: dados?._id || registro?._id || dados?.id || registro?.id || '',
        _childrens: childrens,
    };

};

const normalizaListaEntity = (lista = []) => {
    return Array.isArray(lista) ? lista.map((item) => normalizaRegistroEntity(item)) : [];
};

const temTexto = (value = null) => value !== undefined && value !== null && String(value).trim() !== '';

const dataAtualISO = () => new Date().toISOString();

const toISODateTime = (value = '', fallbackNow = false) => {
    const texto = String(value || '').trim();
    if (!texto) return fallbackNow ? dataAtualISO() : '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(texto)) return `${texto}T03:00:00.000Z`;
    return texto;
};

const extraiDataDaResposta = (response = {}) => {
    const bruto = response?.data;

    if (ehObjeto(bruto) && bruto?.success === true && ehObjeto(bruto?.data)) {
        return bruto.data;
    }

    if (ehObjeto(bruto?.data)) {
        return bruto.data;
    }

    return bruto || {};
};

const limpaCamposInternos = (value = null) => {
    if (Array.isArray(value)) {
        return value.map((item) => limpaCamposInternos(item));
    }

    if (!ehObjeto(value)) {
        return value;
    }

    const sanitized = {};

    Object.entries(value).forEach(([key, itemValue]) => {
        if (String(key).startsWith('_')) return;
        sanitized[key] = limpaCamposInternos(itemValue);
    });

    return sanitized;
};

const filtraPayloadPersistenciaItemDaOS = (payload = {}) => {
    const camposPermitidos = new Set([
        'id',
        'sequencia',
        'item',
        'itemCodigo',
        'itemCodigoExt',
        'status',
        'destino',
        'descricao',
        'quantidade',
        'quantidadeCompra',
        'quantidadeParaEntregar',
        'quantidadeEntregue',
        'quantidadeParaEmbarcar',
        'quantidadeEmbarcada',
        'unidade',
        'unidadeSigla',
        'unidadeCompra',
        'unidadeCompraSigla',
        'pesoTotal',
        'valorUnitario',
        'valorTotal',
        'dataSolicitacao',
        'dataNecessidade',
        'dataLiberacaoAlmoxarifado',
        'dataLiberacaoCompras',
        'dataEntrega',
        'desenhoFabricacao',
        'desenhoMontagem',
        'fornecedor',
        'fornecedorMaterialFornecido',
        'precoUnitarioComImpostos',
        'precoUnitarioComImpostosPorcentagemDoDesconto',
        'precoUnitarioComImpostosComDesconto',
        'precoTotalComImpostos',
        'temISS',
        'porcentagemISS',
        'porcentagemPIS',
        'valorPIS',
        'porcentagemCOFINS',
        'valorCOFINS',
        'temICMS',
        'porcentagemICMS',
        'valorICMS',
        'temIPI',
        'porcentagemIPI',
        'valorIPI',
        'precoTotalSemImpostosComIPI',
        'precoTotalComImpostosComIPI',
    ]);

    const payloadFiltrado = Object.fromEntries(
        Object.entries(payload || {}).filter(([key]) => camposPermitidos.has(key))
    );

    const removeSeVazio = ['unidadeCompra', 'fornecedor', 'fornecedorMaterialFornecido'];
    removeSeVazio.forEach((campo) => {
        if (String(payloadFiltrado?.[campo] || '').trim() === '') {
            delete payloadFiltrado[campo];
        }
    });

    if (!payloadFiltrado?.unidadeCompra) {
        delete payloadFiltrado.unidadeCompraSigla;
    }

    if (!payloadFiltrado?.fornecedor) {
        delete payloadFiltrado.fornecedorMaterialFornecido;
    }

    return payloadFiltrado;
};

const normalizaPayloadItemDaOS = (item = {}, ordemDeServicoId = '') => {
    const itemNormalizado = normalizaRegistroEntity(item);

    const payload = {
        id: pegaId(itemNormalizado?.id || itemNormalizado?._id),
        sequencia: itemNormalizado?.sequencia || itemNormalizado?.sequence || '',
        item: pegaId(itemNormalizado?.item || itemNormalizado?.itemId || itemNormalizado?.itemID),
        itemCodigo: itemNormalizado?.itemCodigo || itemNormalizado?.codigoItem || '',
        itemCodigoExt: itemNormalizado?.itemCodigoExt || itemNormalizado?.itemCodigo || itemNormalizado?.codigoItem || '',
        descricao: itemNormalizado?.descricao || itemNormalizado?.description || '',
        status: itemNormalizado?.status || itemNormalizado?.situacao || 'ED',
        destino: itemNormalizado?.destino || itemNormalizado?.destination || 'ND',
        unidade: pegaId(itemNormalizado?.unidade || itemNormalizado?.unidadeId || itemNormalizado?.unidadeID),
        unidadeSigla: itemNormalizado?.unidadeSigla || itemNormalizado?.siglaUnidade || '',
        unidadeCompra: pegaId(itemNormalizado?.unidadeCompra || itemNormalizado?.unidadeCompraId || itemNormalizado?.unidadeCompraID),
        unidadeCompraSigla: itemNormalizado?.unidadeCompraSigla || itemNormalizado?.siglaUnidadeCompra || '',
        quantidade: Number(itemNormalizado?.quantidade ?? itemNormalizado?.qtd ?? itemNormalizado?.quantidadeSolicitada ?? 0),
        quantidadeCompra: Number(itemNormalizado?.quantidadeCompra ?? itemNormalizado?.qtdComprada ?? itemNormalizado?.quantidadeComprada ?? 0),
        quantidadeParaEntregar: Number(itemNormalizado?.quantidadeParaEntregar ?? itemNormalizado?.qtdParaEntregar ?? 0),
        quantidadeEntregue: Number(itemNormalizado?.quantidadeEntregue ?? itemNormalizado?.qtdEntregue ?? 0),
        quantidadeParaEmbarcar: Number(itemNormalizado?.quantidadeParaEmbarcar ?? itemNormalizado?.qtdParaEmbarcar ?? itemNormalizado?.quantidade ?? 0),
        quantidadeEmbarcada: Number(itemNormalizado?.quantidadeEmbarcada ?? itemNormalizado?.qtdEmbarcada ?? 0),
        pesoTotal: Number(itemNormalizado?.pesoTotal ?? itemNormalizado?.peso ?? 0),
        valorUnitario: Number(itemNormalizado?.valorUnitario ?? itemNormalizado?.valorUnit ?? itemNormalizado?.precoUnitario ?? 0),
        valorTotal: Number(itemNormalizado?.valorTotal ?? itemNormalizado?.precoTotal ?? 0),
        dataSolicitacao: toISODateTime(itemNormalizado?.dataSolicitacao, true),
        dataNecessidade: toISODateTime(itemNormalizado?.dataNecessidade || itemNormalizado?.dataPrevisao || ''),
        dataLiberacaoAlmoxarifado: toISODateTime(itemNormalizado?.dataLiberacaoAlmoxarifado || ''),
        dataLiberacaoCompras: toISODateTime(itemNormalizado?.dataLiberacaoCompras || ''),
        dataEntrega: toISODateTime(itemNormalizado?.dataEntrega || itemNormalizado?.deliveryDate || ''),
        desenhoFabricacao: itemNormalizado?.desenhoFabricacao || '',
        desenhoMontagem: itemNormalizado?.desenhoMontagem || '',
        fornecedor: pegaId(itemNormalizado?.fornecedor || itemNormalizado?.supplier || ''),
        porcentagemPIS: Number(itemNormalizado?.porcentagemPIS ?? 1.65),
        porcentagemCOFINS: Number(itemNormalizado?.porcentagemCOFINS ?? 7.6),
        precoUnitarioComImpostos: Number(itemNormalizado?.precoUnitarioComImpostos ?? 0),
        precoUnitarioComImpostosPorcentagemDoDesconto: Number(itemNormalizado?.precoUnitarioComImpostosPorcentagemDoDesconto ?? 0),
        precoUnitarioComImpostosComDesconto: Number(itemNormalizado?.precoUnitarioComImpostosComDesconto ?? 0),
        precoTotalComImpostos: Number(itemNormalizado?.precoTotalComImpostos ?? 0),
        temISS: !!itemNormalizado?.temISS,
        porcentagemISS: Number(itemNormalizado?.porcentagemISS ?? 0),
        valorPIS: Number(itemNormalizado?.valorPIS ?? 0),
        valorCOFINS: Number(itemNormalizado?.valorCOFINS ?? 0),
        temICMS: !!itemNormalizado?.temICMS,
        porcentagemICMS: Number(itemNormalizado?.porcentagemICMS ?? 0),
        valorICMS: Number(itemNormalizado?.valorICMS ?? 0),
        temIPI: !!itemNormalizado?.temIPI,
        porcentagemIPI: Number(itemNormalizado?.porcentagemIPI ?? 0),
        valorIPI: Number(itemNormalizado?.valorIPI ?? 0),
        precoTotalSemImpostosComIPI: Number(itemNormalizado?.precoTotalSemImpostosComIPI ?? 0),
        precoTotalComImpostosComIPI: Number(itemNormalizado?.precoTotalComImpostosComIPI ?? 0),
    };

    if (Array.isArray(itemNormalizado?.itens)) {
        payload.itens = itemNormalizado.itens;
    }

    if (itemNormalizado?.atual !== undefined) {
        payload.atual = Number(itemNormalizado.atual) || 0;
    }

    const ordemId = pegaId(ordemDeServicoId || itemNormalizado?.ordemDeServico || itemNormalizado?.os || itemNormalizado?.ordemServico);
    if (ordemId) {
        payload.ordemDeServico = ordemId;
    }

    // Remove campos com valor null ou undefined, MAS PRESERVA campos de desenho vazios para permitir limpeza
    const camposDesenho = new Set(['desenhoMontagem', 'desenhoFabricacao']);
    Object.keys(payload).forEach((key) => {
        // Nunca remova campos de desenho - mesmo vazios, eles indicam vontade de atualizar/limpar
        if (camposDesenho.has(key)) return;
        
        if (payload[key] === undefined || payload[key] === null || (typeof payload[key] === 'string' && payload[key].trim() === '')) {
            delete payload[key];
        }
    });

    if (!ehIdPersistido(payload.id)) {
        delete payload.id;
    }

    return payload;
};

const montaPayloadLegadoInsercaoItemDaOS = (payload = {}) => {
    const itensContexto = Array.isArray(payload?.itens) ? payload.itens : [];

    return {
        status: payload?.status || 'ED',
        destino: payload?.destino || 'ND',
        pesoTotal: Number(payload?.pesoTotal ?? 1),
        dataSolicitacao: payload?.dataSolicitacao || dataAtualISO(),
        porcentagemPIS: Number(payload?.porcentagemPIS ?? 1.65),
        porcentagemCOFINS: Number(payload?.porcentagemCOFINS ?? 7.6),
        sequencia: payload?.sequencia || '',
        dataNecessidade: payload?.dataNecessidade || '',
        dataLiberacaoAlmoxarifado: payload?.dataLiberacaoAlmoxarifado || '',
        dataLiberacaoCompras: payload?.dataLiberacaoCompras || '',
        dataEntrega: payload?.dataEntrega || '',
        message: payload?.message || 'SEQ OS-ITEM',
        itens: itensContexto,
        atual: Number(payload?.atual ?? itensContexto.length ?? 0),
        item: payload?.item || '',
        descricao: payload?.descricao || '',
        unidade: payload?.unidade || '',
        quantidade: Number(payload?.quantidade ?? 0),
        quantidadeParaEmbarcar: Number(payload?.quantidadeParaEmbarcar ?? payload?.quantidade ?? 0),
        valorTotal: Number(payload?.valorTotal ?? 0),
        valorUnitario: Number(payload?.valorUnitario ?? 0),
    };
};

const buscaRegistroPorSchema = async (schema, id = '') => {
    if (!id) return {};

    try {
        const service = new EntityService(schema);
        const response = await service.getById(id);
        return normalizaRegistroEntity(response?.data?.data || response?.data || {});
    } catch (e) {
        try {
            const response = await ERP.getOneById(schema, id);
            return normalizaRegistroEntity(response?.data || {});
        } catch (erro) {
            return { id, _id: id };
        }
    }
};

const enriqueceItemDaOS = async (item = {}, cache = { itens: new Map(), unidades: new Map() }) => {
    const itemNormalizado = normalizaRegistroEntity(item);

    const itemId = pegaId(itemNormalizado?.item || itemNormalizado?.itemId || itemNormalizado?.itemID);
    const unidadeId = pegaId(itemNormalizado?.unidade || itemNormalizado?.unidadeId || itemNormalizado?.unidadeID);
    const unidadeCompraId = pegaId(itemNormalizado?.unidadeCompra || itemNormalizado?.unidadeCompraId || itemNormalizado?.unidadeCompraID);

    let itemRelacionamento = {};
    let unidadeRelacionamento = {};
    let unidadeCompraRelacionamento = {};

    if (itemId) {
        if (cache.itens.has(itemId)) {
            itemRelacionamento = cache.itens.get(itemId) || {};
        } else {
            itemRelacionamento = await buscaRegistroPorSchema(Schema.ITEM, itemId);
            cache.itens.set(itemId, itemRelacionamento);
        }
    }

    if (unidadeId) {
        if (cache.unidades.has(unidadeId)) {
            unidadeRelacionamento = cache.unidades.get(unidadeId) || {};
        } else {
            unidadeRelacionamento = await buscaRegistroPorSchema(Schema.ORDEM_DE_SERVICO_ITEM_UNIDADE, unidadeId);
            cache.unidades.set(unidadeId, unidadeRelacionamento);
        }
    }

    if (unidadeCompraId) {
        if (cache.unidades.has(unidadeCompraId)) {
            unidadeCompraRelacionamento = cache.unidades.get(unidadeCompraId) || {};
        } else {
            unidadeCompraRelacionamento = await buscaRegistroPorSchema(Schema.ORDEM_DE_SERVICO_ITEM_UNIDADE, unidadeCompraId);
            cache.unidades.set(unidadeCompraId, unidadeCompraRelacionamento);
        }
    }

    return {
        ...itemNormalizado,
        item: itemId || itemNormalizado?.item || '',
        unidade: unidadeId || itemNormalizado?.unidade || '',
        unidadeCompra: unidadeCompraId || itemNormalizado?.unidadeCompra || '',
        itemCodigo: itemNormalizado?.itemCodigo || itemNormalizado?.codigoItem || itemRelacionamento?.code || itemRelacionamento?.codigo || '',
        itemCodigoExt: itemNormalizado?.itemCodigoExt || itemNormalizado?.itemCodigo || itemNormalizado?.codigoItem || itemRelacionamento?.codeExt || itemRelacionamento?.code || itemRelacionamento?.codigo || '',
        descricao: itemNormalizado?.descricao || itemNormalizado?.description || itemRelacionamento?.description || itemRelacionamento?.descricao || '',
        unidadeSigla: itemNormalizado?.unidadeSigla || itemNormalizado?.siglaUnidade || unidadeRelacionamento?.sigla || unidadeRelacionamento?.code || '',
        unidadeCompraSigla: itemNormalizado?.unidadeCompraSigla || itemNormalizado?.siglaUnidadeCompra || unidadeCompraRelacionamento?.sigla || unidadeCompraRelacionamento?.code || '',
    };
};

const resolveIdItemExistenteDaOS = async (service, ordemId = '', payload = {}) => {
    if (!ordemId) return '';

    const sequencia = String(payload?.sequencia || '').trim();
    const itemId = String(pegaId(payload?.item) || '').trim();

    const itensAtuais = await service.pegaItensDaOrdemDeServico(ordemId, []);
    const lista = Array.isArray(itensAtuais?.data) ? itensAtuais.data : [];
    if (lista.length === 0) return '';

    const normalizados = lista.map((item = {}) => normalizaRegistroEntity(item));

    if (sequencia) {
        const porSequenciaEItem = normalizados.find((itemAtual = {}) => {
            const sequenciaAtual = String(itemAtual?.sequencia || itemAtual?.sequence || '').trim();
            const itemAtualId = String(pegaId(itemAtual?.item || itemAtual?.itemId || itemAtual?.itemID) || '').trim();
            if (!sequenciaAtual || sequenciaAtual !== sequencia) return false;
            if (!itemId) return true;
            return itemAtualId === itemId;
        });

        const idEncontrado = pegaId(porSequenciaEItem?.id || porSequenciaEItem?._id);
        if (ehIdPersistido(idEncontrado)) return idEncontrado;

        const porSequencia = normalizados.find((itemAtual = {}) => String(itemAtual?.sequencia || itemAtual?.sequence || '').trim() === sequencia);
        const idSequencia = pegaId(porSequencia?.id || porSequencia?._id);
        if (ehIdPersistido(idSequencia)) return idSequencia;
    }

    return '';
};

const resolveItemPersistidoDaOS = async (service, ordemId = '', payload = {}) => {
    if (!ordemId) return {};

    const idDireto = await resolveIdItemExistenteDaOS(service, ordemId, payload);
    if (ehIdPersistido(idDireto)) {
        const responseDireto = await service.pegaItemDaOS(idDireto);
        return normalizaRegistroEntity(responseDireto?.data || {});
    }

    const itensAtuais = await service.pegaItensDaOrdemDeServico(ordemId, []);
    const lista = Array.isArray(itensAtuais?.data) ? itensAtuais.data : [];
    if (lista.length === 0) return {};

    const sequencia = String(payload?.sequencia || '').trim();
    const descricao = String(payload?.descricao || '').trim().toLowerCase();
    const itemId = String(pegaId(payload?.item) || '').trim();

    const normalizados = lista.map((item = {}) => normalizaRegistroEntity(item));

    const candidatos = normalizados.filter((itemAtual = {}) => {
        const idAtual = pegaId(itemAtual?.id || itemAtual?._id);
        if (!ehIdPersistido(idAtual)) return false;

        const sequenciaAtual = String(itemAtual?.sequencia || itemAtual?.sequence || '').trim();
        const itemAtualId = String(pegaId(itemAtual?.item || itemAtual?.itemId || itemAtual?.itemID) || '').trim();
        const descricaoAtual = String(itemAtual?.descricao || itemAtual?.description || '').trim().toLowerCase();

        if (sequencia && sequenciaAtual !== sequencia) return false;
        if (itemId && itemAtualId && itemAtualId !== itemId) return false;
        if (descricao && descricaoAtual && descricaoAtual !== descricao) return false;

        return true;
    });

    if (candidatos.length === 0) return {};

    const ordenados = candidatos.sort((a, b) => {
        const dataA = new Date(a?.updatedAt || a?.createdAt || a?.dataSolicitacao || 0).getTime();
        const dataB = new Date(b?.updatedAt || b?.createdAt || b?.dataSolicitacao || 0).getTime();
        return dataB - dataA;
    });

    return normalizaRegistroEntity(ordenados[0] || {});
};

export default {

    /*==================================================================*/
    /*================== PEGA VIEW ORDEM DE SERVICO ====================*/
    /*==================================================================*/

    async pegaViewOrdemDeServico(){

        try{

            const entityService = new EntityService(Schema.ORDEM_DE_SERVICO);
            const response = await entityService.getAll(1, 100000, null, null, null, null);

            return {
                status: response?.status || 200,
                data: response?.data?.data?.data || [],
            };

        }catch(e){

            console.log('Erro ao buscar Ordens de Serviço:', e);
            return {
                status: 500,
                data: [],
            };

        }

    },

    /*==================================================================*/
    /*================== PEGA VIEW JOBS ================================*/
    /*==================================================================*/

    async pegaViewJobs(){

        try{
            const normalize = str => (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
            
            const entityService = new EntityService(Schema.JOB);
            const response = await entityService.getAll(1, 100000, null, null, null, null);
            let allJobs = response?.data?.data?.data || [];
            allJobs = allJobs.filter(j => {
                const status = normalize(j.status) || normalize(j.situacao);
                return status === 'em execucao';
            });
            return {
                status: response?.status || 200,
                data: allJobs,
            };
        }catch(e){
            console.log('Erro ao buscar Jobs:', e);
            return {
                status: 500,
                data: [],
            };
        }

    },

    /*==================================================================*/
    /*================== PEGA VIEW JOB ITENS ===========================*/
    /*==================================================================*/

    async pegaViewJobItens(jobId){

        let jobItens;

        try{

            jobItens = await ERP.getManyWithView(View.ORDEM_DE_SERVICO_JOB_ITENS, [
                {
                    name: "job",
                    dataType: "String",
                    value: jobId
                },
            ]);

        }catch(e){

            console.log(e);
            return jobItens;

        }

        return jobItens;

    },

    /*==================================================================*/
    /*================== PEGA VIEW COORDENADORES =======================*/
    /*==================================================================*/

    async pegaViewCoordenadores(){

        try{

            const entityService = new EntityService(Schema.COLABORADOR);
            const response = await entityService.getAll(1, 10000, null, null, null, null);

            return {
                status: response?.status || 200,
                data: response?.data?.data?.data || [],
            };

        }catch(e){

            console.log('Erro ao buscar Coordenadores:', e);
            return {
                status: 500,
                data: [],
            };

        }

    },

    /*==================================================================*/
    /*================== PEGA ITENS DA PROPRIA OS ======================*/
    /*==================================================================*/

    async pegaItensDaOS(itensDaOSIds = []){

        const ids = normalizaIdList(itensDaOSIds);

        if (ids.length === 0) {
            return { status: 200, data: [] };
        }

        let itensDaOS;

        try{

            itensDaOS = await ERP.getManyById(Schema.ORDEM_DE_SERVICO_ITENS, ids);

            const itensNormalizados = normalizaListaEntity(itensDaOS?.data);

            let itensOrdenados = ids
                .map((id) => itensNormalizados.find((item) => pegaId(item) === id) || { id, _id: id })
                .map((item) => normalizaRegistroEntity(item));

            const possuiItensSemDados = itensOrdenados.some((item) => !temDadosDeItemDaOS(item));

            if (possuiItensSemDados) {
                const itemService = new EntityService(Schema.ORDEM_DE_SERVICO_ITENS);

                const itensResolvidos = await Promise.allSettled(
                    ids.map(async (id, index) => {
                        const itemAtual = itensOrdenados[index] || { id, _id: id };
                        if (temDadosDeItemDaOS(itemAtual)) return itemAtual;

                        try {
                            const response = await itemService.getById(id);
                            return normalizaRegistroEntity(response?.data?.data || response?.data || {});
                        } catch (e) {
                            try {
                                const response = await ERP.getOneById(Schema.ORDEM_DE_SERVICO_ITENS, id);
                                return normalizaRegistroEntity(response?.data || {});
                            } catch (erro) {
                                return { id, _id: id };
                            }
                        }
                    })
                );

                itensOrdenados = itensResolvidos.map((result, index) => {
                    if (result.status === 'fulfilled') return normalizaRegistroEntity(result.value || {});
                    return { id: ids[index], _id: ids[index] };
                });
            }

            const cache = { itens: new Map(), unidades: new Map() };
            const itensEnriquecidos = await Promise.all(
                itensOrdenados.map((item) => enriqueceItemDaOS(item, cache))
            );

            return {
                status: itensDaOS?.status || 200,
                data: itensEnriquecidos,
            };

        }catch(e){

            console.log(e);
            return itensDaOS;

        }

    },

    /*==================================================================*/
    /*================== PEGA OS COMPLETA ==============================*/
    /*==================================================================*/

    async pegaOrdemDeServicoCompleta(ordemDeServicoId = '') {

        if (!ordemDeServicoId) {
            return { status: 200, data: {} };
        }

        let response;

        try {

            const entityService = new EntityService(Schema.ORDEM_DE_SERVICO);
            response = await entityService.getById(ordemDeServicoId);

            const registro = response?.data?.data || response?.data || {};

            return {
                status: response?.status || 200,
                data: normalizaRegistroEntity(registro),
            };

        } catch (e) {

            console.log(e);
            return response;

        }

    },

    /*==================================================================*/
    /*================== PEGA ITENS DA ORDEM DE SERVICO ================*/
    /*==================================================================*/

    async pegaItensDaOrdemDeServico(ordemDeServicoId = '', itensDaOSIds = []) {

        if (!ordemDeServicoId && (!Array.isArray(itensDaOSIds) || itensDaOSIds.length === 0)) {
            return { status: 200, data: [] };
        }

        try {

            if (ordemDeServicoId) {

                const ordemDeServico = await this.pegaOrdemDeServicoCompleta(ordemDeServicoId);
                const childrens = ordemDeServico?.data?._childrens || {};

                const itensChildrens = childrens?.OrdemDeServicoItens || childrens?.ordemDeServicoItens || [];
                if (Array.isArray(itensChildrens) && itensChildrens.length > 0) {

                    const itensChildrensNormalizados = normalizaListaEntity(itensChildrens);
                    const childrensTemDados = itensChildrensNormalizados.some((item) => temDadosDeItemDaOS(item));

                    if (childrensTemDados) {
                        return {
                            status: ordemDeServico?.status || 200,
                            data: itensChildrensNormalizados,
                        };
                    }

                    const idsDosChildrens = itensChildrensNormalizados.map((item) => pegaId(item)).filter((id) => !!id);
                    if (idsDosChildrens.length > 0) {
                        const itensPorIds = await this.pegaItensDaOS(idsDosChildrens);
                        const temDados = Array.isArray(itensPorIds?.data) && itensPorIds.data.some((item) => temDadosDeItemDaOS(item));
                        if (temDados) return itensPorIds;
                    }

                }

                const ids = Array.isArray(ordemDeServico?.data?.itens) && ordemDeServico.data.itens.length > 0
                    ? ordemDeServico.data.itens
                    : itensDaOSIds;

                const itensPorIds = await this.pegaItensDaOS(ids);
                const temDadosPorIds = Array.isArray(itensPorIds?.data) && itensPorIds.data.some((item) => temDadosDeItemDaOS(item));
                if (temDadosPorIds) {
                    return itensPorIds;
                }

                const tentativasPorRelacionamento = await Promise.allSettled([
                    ERP.getManyByValue(Schema.ORDEM_DE_SERVICO_ITENS, 'ordemDeServico', ordemDeServicoId),
                    ERP.getManyByValue(Schema.ORDEM_DE_SERVICO_ITENS, 'os', ordemDeServicoId),
                    ERP.getManyByValue(Schema.ORDEM_DE_SERVICO_ITENS, 'ordemServico', ordemDeServicoId),
                ]);

                const melhorTentativa = tentativasPorRelacionamento
                    .filter((result) => result.status === 'fulfilled')
                    .map((result) => result.value)
                    .map((response = {}) => ({
                        status: response?.status || 200,
                        data: normalizaListaEntity(response?.data || []),
                    }))
                    .sort((a, b) => (b?.data?.length || 0) - (a?.data?.length || 0))[0];

                if (Array.isArray(melhorTentativa?.data) && melhorTentativa.data.length > 0) {
                    const cache = { itens: new Map(), unidades: new Map() };
                    const itensEnriquecidos = await Promise.all(
                        melhorTentativa.data.map((item = {}) => enriqueceItemDaOS(item, cache))
                    );

                    return {
                        status: melhorTentativa.status || 200,
                        data: itensEnriquecidos,
                    };
                }

                return itensPorIds;

            }

            return await this.pegaItensDaOS(itensDaOSIds);

        } catch (e) {

            console.log(e);
            return { status: 500, data: [] };

        }

    },

    /*==================================================================*/
    /*================== PEGA OS FILHAS ================================*/
    /*==================================================================*/

    async pegaOsFilhas(ordemDeServicoId = '') {

        if (!ordemDeServicoId) {
            return { status: 200, data: [] };
        }

        try {

            const ordemDeServico = await this.pegaOrdemDeServicoCompleta(ordemDeServicoId);
            const childrens = ordemDeServico?.data?._childrens || {};

            const osFilhas = childrens?.OrdemDeServico || childrens?.ordemDeServico || [];
            if (Array.isArray(osFilhas) && osFilhas.length > 0) {
                return {
                    status: ordemDeServico?.status || 200,
                    data: normalizaListaEntity(osFilhas),
                };
            }

            const ordensDeServico = await this.pegaViewOrdemDeServico();
            const lista = Array.isArray(ordensDeServico?.data) ? ordensDeServico.data : [];

            return {
                status: ordensDeServico?.status || 200,
                data: lista.filter((item = {}) => pegaId(item?.osMae) === ordemDeServicoId),
            };

        } catch (e) {

            console.log(e);
            return { status: 500, data: [] };

        }

    },

    /*==================================================================*/
    /*================== PEGA ITEM DA OS ===============================*/
    /*==================================================================*/

    async pegaItemDaOS(itemDaOSId = ""){

        if (!itemDaOSId) {
            return { status: 200, data: {} };
        }

        let itemDaOS;

        try{

            itemDaOS = await ERP.getOneById(Schema.ORDEM_DE_SERVICO_ITENS, itemDaOSId);

            const itemDaOSNormalizado = normalizaRegistroEntity(itemDaOS?.data || {});

            if (itemDaOS?.status === 200 && itemDaOSNormalizado?.id) {
                return {
                    status: itemDaOS?.status || 200,
                    data: itemDaOSNormalizado,
                };
            }

            itemDaOS = await ERP.getOneById(Schema.ITEM, itemDaOSId);

            return {
                status: itemDaOS?.status || 200,
                data: normalizaRegistroEntity(itemDaOS?.data || {}),
            };

        }catch(e){

            console.log(e);
            return itemDaOS;

        }

    },

    /*==================================================================*/
    /*================== SALVA ITEM DA OS ==============================*/
    /*==================================================================*/

    async salvaItemDaOS(item = {}, ordemDeServicoId = '') {

        const ordemId = pegaId(ordemDeServicoId);
        if (!ordemId) {
            return { status: 400, data: {}, message: 'Ordem de Serviço não informada' };
        }

        const payload = normalizaPayloadItemDaOS(item, ordemId);
        const entityService = new EntityService(Schema.ORDEM_DE_SERVICO_ITENS);
        let response;

        try {

            let idItem = pegaId(payload?.id);

            if (!ehIdPersistido(idItem)) {
                idItem = await resolveIdItemExistenteDaOS(this, ordemId, payload);
            }

            if (ehIdPersistido(idItem)) {
                const itemPersistidoResponse = await this.pegaItemDaOS(idItem);
                const itemPersistido = normalizaRegistroEntity(itemPersistidoResponse?.data || {});

                const payloadAtualizacao = {
                    ...itemPersistido,
                    ...payload,
                    id: idItem,
                    _id: idItem,
                    ordemDeServico: ordemId,
                };

                const payloadAtualizacaoSanitizado = filtraPayloadPersistenciaItemDaOS(limpaCamposInternos(payloadAtualizacao));

                response = await ERP.putOne(Schema.ORDEM_DE_SERVICO_ITENS, payloadAtualizacaoSanitizado);

                if (response?.status !== 200) {
                    response = await ERP.patchOne(Schema.ORDEM_DE_SERVICO_ITENS, payloadAtualizacaoSanitizado);
                }

                if (response?.status !== 200) {
                    return response || { status: 500, data: {}, message: 'Falha ao atualizar item da OS' };
                }

                const itemAtualizadoResponse = await this.pegaItemDaOS(idItem);
                const itemNormalizado = normalizaRegistroEntity(itemAtualizadoResponse?.data || extraiDataDaResposta(response));
                const itemEnriquecido = await enriqueceItemDaOS(itemNormalizado, { itens: new Map(), unidades: new Map() });

                return {
                    status: response?.status || 200,
                    data: itemEnriquecido,
                };
            }

            const payloadLegado = montaPayloadLegadoInsercaoItemDaOS(payload);
            const camposRelacionamento = ['ordemDeServico', 'os', 'ordemServico'];
            let ultimoErro = null;

            const tentativas = [
                payloadLegado,
                ...camposRelacionamento.map((campo) => ({ ...payloadLegado, [campo]: ordemId })),
                payload,
                ...camposRelacionamento.map((campo) => ({ ...payload, [campo]: ordemId })),
            ];

            for (const tentativa of tentativas) {
                try {
                    const payloadInsercao = { ...tentativa };
                    delete payloadInsercao.id;

                    response = await entityService.insertOrUpdate(payloadInsercao);
                    if (response?.status !== 200) {
                        response = await ERP.postOne(Schema.ORDEM_DE_SERVICO_ITENS, payloadInsercao);
                    }

                    if (response?.status !== 200) {
                        ultimoErro = response;
                        continue;
                    }

                    let itemNormalizado = normalizaRegistroEntity(extraiDataDaResposta(response));
                    const idRetornado = pegaId(itemNormalizado?.id || itemNormalizado?._id);

                    if (!ehIdPersistido(idRetornado)) {
                        const itemResolvido = await resolveItemPersistidoDaOS(this, ordemId, payload);
                        const idResolvido = pegaId(itemResolvido?.id || itemResolvido?._id);

                        if (ehIdPersistido(idResolvido)) {
                            itemNormalizado = itemResolvido;
                        } else {
                            ultimoErro = { status: 422, data: {}, message: 'Item criado sem id persistido para vínculo na OS' };
                            continue;
                        }
                    }

                    const itemEnriquecido = await enriqueceItemDaOS(itemNormalizado, { itens: new Map(), unidades: new Map() });

                    return {
                        status: response?.status || 200,
                        data: itemEnriquecido,
                    };
                } catch (e) {
                    ultimoErro = e;
                }
            }

            console.log(ultimoErro);
            return response || { status: 500, data: {}, message: 'Falha ao criar item da OS' };

        } catch (e) {

            console.log(e);
            return response;

        }

    },

    /*==================================================================*/
    /*================== SINCRONIZA ITENS DA OS ========================*/
    /*==================================================================*/

    async sincronizaItensDaOS(ordemDeServicoId = '', itens = []) {

        const ordemId = pegaId(ordemDeServicoId);
        if (!ordemId) {
            return { status: 400, data: [], ids: [] };
        }

        try {
            const lista = Array.isArray(itens) ? itens : [];
            const itensSincronizados = [];

            for (const itemAtual of lista) {
                const idAtual = pegaId(itemAtual?.id || itemAtual?._id);

                if (!idAtual || String(idAtual).startsWith('tmp-')) {
                    const respostaSalvar = await this.salvaItemDaOS(itemAtual, ordemId);
                    if (respostaSalvar?.status === 200 && respostaSalvar?.data) {
                        itensSincronizados.push(respostaSalvar.data);
                    }
                    continue;
                }

                const itemEnriquecido = await enriqueceItemDaOS(itemAtual, { itens: new Map(), unidades: new Map() });
                itensSincronizados.push(itemEnriquecido);
            }

            const ids = itensSincronizados
                .map((item = {}) => pegaId(item?.id || item?._id))
                .filter((id) => !!id);

            return {
                status: 200,
                data: itensSincronizados,
                ids,
            };

        } catch (e) {

            console.log(e);
            return { status: 500, data: [], ids: [] };

        }

    },

    /*==================================================================*/
    /*================== PEGA HISTORICO DO ITEM DA OS ==================*/
    /*==================================================================*/

    async pegaHistoricoItemDaOS(itemDaOSIds = "", ordemDeServicoId = ""){

        const extraiIdsDoItem = (item = {}) => {
            if (!item || typeof item !== 'object') return [];

            const candidatos = [
                item?.id,
                item?._id,
                item?.valueId,
                item?.ordemDeServicoItem,
                item?.ordemDeServicoItens,
                item?.itemOrdemDeServico,
                item?.itemOrdemDeServicoId,
                item?.ordemDeServicoItem?.id,
                item?.ordemDeServicoItem?._id,
            ];

            return [...new Set(
                candidatos
                    .map((valor) => String(pegaId(valor) || '').trim())
                    .filter(Boolean)
            )];
        };

        const itensComoObjeto = Array.isArray(itemDaOSIds)
            ? itemDaOSIds.filter((item) => item && typeof item === 'object')
            : [];

        const ids = Array.isArray(itemDaOSIds)
            ? [...new Set(itemDaOSIds
                .flatMap((i) => (i && typeof i === 'object')
                    ? extraiIdsDoItem(i)
                    : [String(i || '').trim()]
                )
                .filter(Boolean)
            )]
            : (itemDaOSIds ? [String(itemDaOSIds).trim()] : []);

        const historicoDosChildrens = itensComoObjeto
            .flatMap((item = {}) => {
                const normalizado = normalizaRegistroEntity(item);
                const childrens = normalizado?._childrens || item?._childrens || item?.childrens || {};
                const lista = childrens?.HistoricoOrdemDeServicoItens || childrens?.historicoOrdemDeServicoItens || [];
                return Array.isArray(lista) ? normalizaListaEntity(lista) : [];
            })
            .filter((registro = {}) => !!registro);

        if (historicoDosChildrens.length > 0) {
            const historicoOrdenadoLocal = historicoDosChildrens.sort((a, b) => {
                const dataA = new Date(a?.historicoDataCriacao || 0).getTime();
                const dataB = new Date(b?.historicoDataCriacao || 0).getTime();
                return dataB - dataA;
            });

            return {
                status: 200,
                data: historicoOrdenadoLocal,
            };
        }

        if (ids.length === 0) {
            return { status: 200, data: [] };
        }

        try {
            // Usar o campo correto do relacionamento conforme definido no schema
            const todos = [];

            // Buscar por cada ID do item da OS
            for (const id of ids) {
                try {
                    const response = await ERP.getManyByValue(Schema.HISTORICO_ORDEM_DE_SERVICO_ITEM, 'ordemDeServicoItem', id);
                    
                    if (Array.isArray(response?.data)) {
                        todos.push(...response.data);
                    } else if (Array.isArray(response?.data?.data)) {
                        todos.push(...response.data.data);
                    }
                } catch (erro) {
                    console.error(`Erro ao buscar histórico para item ${id}:`, erro);
                }
            }

            const unicos = [];
            const vistos = new Set();

            todos.forEach((registro = {}) => {
                const chave = String(
                    registro?.id ||
                    registro?._id ||
                    `${registro?.ordemDeServicoItem || ''}-${registro?.historicoDataCriacao || ''}`
                );

                if (!chave || vistos.has(chave)) return;
                vistos.add(chave);
                unicos.push(registro);
            });

            const historicoOrdenado = unicos.sort((a, b) => {
                const dataA = new Date(a?.historicoDataCriacao || 0).getTime();
                const dataB = new Date(b?.historicoDataCriacao || 0).getTime();
                return dataB - dataA;
            });

            return {
                status: 200,
                data: historicoOrdenado,
            };

        } catch(e) {

            console.log('Erro ao buscar histórico da OS:', e);
            return { status: 500, data: [] };

        }
    },

    /*==================================================================*/
    /*================== CRIA ORDEM DE SERVICO =========================*/
    /*==================================================================*/

    async criaOrdemDeServico(payload = {}){

        let usuarioStore = useUsuarioStore();

        let response;

        try{
            const owners = buildOwnersWithAdmin({
                loggedUserId: usuarioStore?.usuario?.id || '',
                owners: payload?.owners,
            });

            response = await ERP.postOne(Schema.ORDEM_DE_SERVICO, {
                ...payload,
                owners,
            });

        }catch(e){

            console.log(e);
            return response;

        }

        return response;

    },

    /*==================================================================*/
    /*================== EDITA ORDEM DE SERVICO ========================*/
    /*==================================================================*/
    async editaOrdemDeServico(payload = {}) {
        // Ajuste para seguir o padrão do legado
        let response;
        try {
            const usuarioStore = useUsuarioStore();
            const ordemId = pegaId(payload?.id);
            if (!ordemId) {
                return { status: 400, data: {}, message: 'Ordem de Serviço não informada' };
            }

            // Busca a OS atual para preencher campos obrigatórios
            const ordemAtual = await ERP.getOneById(Schema.ORDEM_DE_SERVICO, ordemId);
            const os = ordemAtual?.data || {};

            // Owners como array de objetos
            let owners = os.owners || [];
            if (Array.isArray(payload.owners) && payload.owners.length > 0) {
                owners = payload.owners;
            }
            owners = buildOwnersWithAdmin({
                loggedUserId: usuarioStore?.usuario?.id || '',
                owners,
            });

            // Monta payload igual ao legado
            const payloadCompleto = {
                codigo: payload.codigo ?? os.codigo ?? '',
                job: payload.job ?? os.job ?? '',
                jobCodigo: payload.jobCodigo ?? os.jobCodigo ?? '',
                jobItem: payload.jobItem ?? os.jobItem ?? '',
                jobItemSequencia: payload.jobItemSequencia ?? os.jobItemSequencia ?? '',
                coordenadorDeProjetos: pegaId(payload.coordenadorDeProjetos ?? os.coordenadorDeProjetos ?? ''),
                coordenadorDeProjetosNome: payload.coordenadorDeProjetosNome ?? os.coordenadorDeProjetosNome ?? '',
                descricao: payload.descricao ?? os.descricao ?? '',
                osTipo: payload.osTipo ?? os.osTipo ?? '',
                osQuantidade: payload.osQuantidade ?? os.osQuantidade ?? 0,
                valorDisponivel: payload.valorDisponivel ?? os.valorDisponivel ?? 0,
                dataSolicitacao: payload.dataSolicitacao ?? os.dataSolicitacao ?? '',
                dataEncerramentoPrevista: payload.dataEncerramentoPrevista ?? os.dataEncerramentoPrevista ?? '',
                dataEncerramento: payload.dataEncerramento ?? os.dataEncerramento ?? '',
                dataDetalhamentoPrevista: payload.dataDetalhamentoPrevista ?? os.dataDetalhamentoPrevista ?? '',
                dataDetalhamento: payload.dataDetalhamento ?? os.dataDetalhamento ?? '',
                possuiDetalhamento: payload.possuiDetalhamento ?? os.possuiDetalhamento ?? false,
                dataMontagemPrevista: payload.dataMontagemPrevista ?? os.dataMontagemPrevista ?? '',
                dataMontagem: payload.dataMontagem ?? os.dataMontagem ?? '',
                possuiMontagem: payload.possuiMontagem ?? os.possuiMontagem ?? false,
                dataFabricacaoPrevista: payload.dataFabricacaoPrevista ?? os.dataFabricacaoPrevista ?? '',
                dataFabricacao: payload.dataFabricacao ?? os.dataFabricacao ?? '',
                itens: Array.isArray(payload.itens) ? payload.itens : os.itens ?? [],
                owners,
                id: ordemId,
                createAt: os.createAt ?? '',
                createBy: os.createBy ?? '',
                updateAt: os.updateAt ?? '',
                updateBy: os.updateBy ?? '',
                deleteAt: os.deleteAt ?? '',
                deleteBy: os.deleteBy ?? '',
                isDeleted: os.isDeleted ?? false,
            };

            // Remove campos de auditoria do payload antes de enviar
            const {
                createAt,
                createBy,
                updateAt,
                updateBy,
                deleteAt,
                deleteBy,
                isDeleted,
                ...payloadSemAuditoria
            } = payloadCompleto;

            // Remove campos com valor null ou string vazia
            const payloadFinal = {};
            Object.keys(payloadSemAuditoria).forEach((key) => {
                const value = payloadSemAuditoria[key];
                if (value !== null && !(typeof value === 'string' && value.trim() === '')) {
                    payloadFinal[key] = value;
                }
            });
            if (!temTexto(payloadFinal.coordenadorDeProjetos)) {
                delete payloadFinal.coordenadorDeProjetos;
            }

            // LOG antes do update da OS
            console.log('[OS][editaOrdemDeServico] Vai enviar update da OS', {
                schema: Schema.ORDEM_DE_SERVICO,
                payload: payloadFinal
            });
            // PUT usando método já implementado
            response = await ERP.putOne(Schema.ORDEM_DE_SERVICO, payloadFinal);
            // LOG depois do update da OS
            console.log('[OS][editaOrdemDeServico] Resposta update da OS', response);

        } catch (e) {
            console.log(e);
            return response;
        }
        return response;
    },

};
